import { ANONYMIZE_SUBSCRIBER_IMMEDIATELY_ACCESS, SUBSCRIBER_ANONYMIZATION_ACCESS } from '../../security.attributes';
import { BILLING_CONFIGURATION_TYPES } from 'invision-core/src/components/metadata/billingConfiguration/billing.configuration.action.types';
import { CODES } from 'invision-core/src/components/metadata/codes/codes.constants';
import CoreLocaleKeys from 'invision-core/src/locales/core.locale.keys';
import { createSelector } from 'reselect';
import filter from 'ramda/src/filter';
import find from 'ramda/src/find';
import mapKeys from 'lodash/mapKeys';
import { hasAccess } from 'invision-core/src/components/security/permission.service';
import i18n from 'invision-core/src/components/i18n/i18n';
import { IsDbss } from 'invision-core/src/components/session/businessunit.selectors';
import LocaleKeys from '../../locales/keys';
import { MetadataCodeSelector, MetadataCodeTypeSelector } from 'invision-core/src/components/metadata/codes/codes.selectors';
import { BillingConfigurationSelector } from 'invision-core/src/components/metadata/billingConfiguration/billing.configuration.selectors';
import pathOr from 'ramda/src/pathOr';
import propEq from 'ramda/src/propEq';
import { taxLevelExemptions as taxLevelExemptionsHelper } from '../createCustomer/tax.model.helper';
import { UserSecurityRolesSelector, UserSecurityAttributesSelector } from 'invision-core/src/components/session/session.selectors';
var WHOLESALE_FLAG = 'WholesaleFlag';
import { CUSTOMER_CATEGORY, INVOICE_DELIVERY_METHODS, OPT_OUT_OPTIONS } from './create.customer.popup.constants';
import { SelectedCustomerSelector, SelectedCustomerCategorySelector, CurrentCustomerSelector, CurrentCustomerIsExternallyManagedARSelector, HasConvergentBillerIdSelector } from '../../reducers/selectors/customer.selectors';
import { PROFILE_OPTION_REACTIVATE_ID, PROFILE_OPTION_SCHEDULE_ANONYMIZATION_ID, PROFILE_OPTION_ANONYMIZE_IMMEDIATELY_ID, PROFILE_REMOVED_STATUS } from '../customer/profile/profile.constants';
import { CUSTOMER_STATE, PROSPECT_LITE_CUSTOMER } from '../shared/constants/customer.constants';
import { convertStringToNumber } from 'invision-core/src/components/helpers/conversion.helper';
import { createImmutableSelector } from 'invision-core/src/utilities/create.immutable.selector';
import { getBoolOrDefault } from 'invision-core/src/components/helpers/bool.helper';
import { findByKey, findByValue } from 'invision-core/src/utilities/ramda.helper';
import { EMPTY_ARRAY } from '../../reducers/constants/common.constants';
import { INVOICE_DETAIL_LEVEL } from '../../customercare.constants';
export var CurrentCustomerProfileMoreMenuItemsSelector = createSelector([CurrentCustomerSelector, IsDbss, UserSecurityAttributesSelector], function (currentCustomer, isDbss, userSecurityAttributes) {
  var moreOptionsMenuItems = [];
  var PROFILE_MORE_MENU_SCHEDULE_ANONYMIZATION_OPTION = {
    id: PROFILE_OPTION_SCHEDULE_ANONYMIZATION_ID,
    title: i18n.translate(LocaleKeys.PROFILE_PAGE.SCHEDULE_FOR_ANONYMIZATION)
  };
  var PROFILE_MORE_MENU_ANONYMIZE_IMMEDIATELY_OPTION = {
    id: PROFILE_OPTION_ANONYMIZE_IMMEDIATELY_ID,
    title: i18n.translate(LocaleKeys.PROFILE_PAGE.ANONYMIZE_IMMEDIATELY)
  };
  var PROFILE_MORE_MENU_REACTIVATE_OPTION = {
    id: PROFILE_OPTION_REACTIVATE_ID,
    title: i18n.translate(LocaleKeys.REACTIVATE)
  };
  if (currentCustomer && currentCustomer.Status && userSecurityAttributes) {
    if (!currentCustomer.CompletedAnonymization && shouldDisplayAnonymizeOptions(isDbss, currentCustomer.State)) {
      if (!currentCustomer.ScheduledAnonymization && hasAccess(userSecurityAttributes, SUBSCRIBER_ANONYMIZATION_ACCESS)) {
        moreOptionsMenuItems.push(PROFILE_MORE_MENU_SCHEDULE_ANONYMIZATION_OPTION);
      }
      if (hasAccess(userSecurityAttributes, ANONYMIZE_SUBSCRIBER_IMMEDIATELY_ACCESS)) {
        moreOptionsMenuItems.push(PROFILE_MORE_MENU_ANONYMIZE_IMMEDIATELY_OPTION);
      }
    }
    if (!currentCustomer.CompletedAnonymization && !isDbss && currentCustomer.Status && currentCustomer.Status === PROFILE_REMOVED_STATUS) {
      moreOptionsMenuItems.push(PROFILE_MORE_MENU_REACTIVATE_OPTION);
    }
  }
  return moreOptionsMenuItems;
});
export var InvoiceDisplaySelector = createSelector([BillingConfigurationSelector], function (billingConfiguration) {
  var selectOption = {
    Name: i18n.translate(CoreLocaleKeys.SELECT),
    Value: undefined // must be undefined for required validation to work
  };
  var billingConfigurationOption = billingConfiguration.asMutable({
    deep: true
  });
  var invoiceDisplays = pathOr([], [BILLING_CONFIGURATION_TYPES.INVOICE_DISPLAY_CODE, 'items'], billingConfigurationOption);
  return [selectOption].concat(invoiceDisplays.map(function (item) {
    return {
      Name: item.DisplayName,
      Value: item.Name
    };
  }));
});
export var BillCyclesSelector = createSelector([BillingConfigurationSelector], function (billingConfiguration) {
  var selectOption = {
    Name: i18n.translate(CoreLocaleKeys.SELECT),
    Value: undefined // must be undefined for required validation to work
  };
  var billingConfigurationOption = billingConfiguration.asMutable({
    deep: true
  });
  var billCycles = pathOr([], [BILLING_CONFIGURATION_TYPES.BILL_CYCLES_CODE, 'items'], billingConfigurationOption);
  return [selectOption].concat(billCycles.map(function (item) {
    return {
      Name: item.DisplayName,
      Value: item.Name
    };
  }));
});
export var TaxCategoryExemptionsSelector = createSelector([BillingConfigurationSelector], function (billingConfiguration) {
  var taxCategoryExemptions = pathOr([], [BILLING_CONFIGURATION_TYPES.TAX_EXEMPTION_CATEGORY_CODE, 'items'], billingConfiguration);
  return taxCategoryExemptions.map(function (item) {
    return {
      id: item.Name,
      selected: false,
      text: item.DisplayName
    };
  });
});
export var TaxExemptionTypeCodesSelector = createSelector([BillingConfigurationSelector], function (billingConfiguration) {
  var taxExemptionTypeCodes = pathOr([], [BILLING_CONFIGURATION_TYPES.TAX_EXEMPTION_TYPE_CODE, 'items'], billingConfiguration.asMutable({
    deep: true
  }));
  return taxExemptionTypeCodes.sort(function (a, b) {
    return b.DisplayName > a.DisplayName ? -1 : 1;
  }).map(function (item) {
    return {
      Id: item.Name,
      Value: item.DisplayName
    };
  });
});
export var NotificationPreferencesSelector = createImmutableSelector([MetadataCodeTypeSelector(CODES.NotificationCategories), SelectedCustomerSelector], function (notificationCategoryCodeTable, selectedCustomer) {
  if (notificationCategoryCodeTable !== null && notificationCategoryCodeTable !== void 0 && notificationCategoryCodeTable.length) {
    return notificationCategoryCodeTable.reduce(function (acc, notificationCategory) {
      var _selectedCustomer$dat, _selectedCustomer$dat2;
      var optOutOptions = [];
      var selectedCustomerOptOutOptions = selectedCustomer === null || selectedCustomer === void 0 ? void 0 : (_selectedCustomer$dat = selectedCustomer.data.NotificationCategoryPreferences) === null || _selectedCustomer$dat === void 0 ? void 0 : (_selectedCustomer$dat2 = _selectedCustomer$dat.find(function (notificationCategoryType) {
        return notificationCategoryType.CustomerNotificationCategoryId === +notificationCategory.Value;
      })) === null || _selectedCustomer$dat2 === void 0 ? void 0 : _selectedCustomer$dat2.OptOutOptions;
      if (selectedCustomerOptOutOptions !== null && selectedCustomerOptOutOptions !== void 0 && selectedCustomerOptOutOptions.length) {
        optOutOptions = selectedCustomerOptOutOptions.map(function (option) {
          if (option === OPT_OUT_OPTIONS.OPT_OUT_EMAIL) {
            return {
              label: i18n.translate(LocaleKeys.NOTIFICATION_PREFERENCES_TYPE.OPT_OUT_EMAIL),
              value: OPT_OUT_OPTIONS.OPT_OUT_EMAIL
            };
          }
          if (option === OPT_OUT_OPTIONS.OPT_OUT_SMS) {
            return {
              label: i18n.translate(LocaleKeys.NOTIFICATION_PREFERENCES_TYPE.OPT_OUT_SMS),
              value: OPT_OUT_OPTIONS.OPT_OUT_SMS
            };
          }
        });
      } else {
        optOutOptions = [{
          label: i18n.translate(LocaleKeys.CREATE_CUSTOMER_FORM.CONTACT_PREFERENCES.CONTACT_METHODS_EMPTY_PLACEHOLDER),
          value: null
        }];
      }
      acc.push({
        customerNotificationCategoryId: notificationCategory.Value,
        customerNotificationCategoryName: notificationCategory.Name,
        optOutOptions: optOutOptions
      });
      return acc;
    }, []);
  }
  return [];
});
export var TaxAssociationCodesSelector = createImmutableSelector([BillingConfigurationSelector], function (billingConfiguration) {
  var taxAssociationCodes = pathOr([], [BILLING_CONFIGURATION_TYPES.TAX_ASSOCICATION_CODE, 'items'], billingConfiguration.asMutable({
    deep: true
  }));
  return taxAssociationCodes.sort(function (a, b) {
    return b.DisplayName > a.DisplayName ? -1 : 1;
  }).map(function (item) {
    return {
      Id: item.Name,
      Value: item.DisplayName
    };
  });
});
export var HomeCountriesStatesSelector = createImmutableSelector(MetadataCodeTypeSelector(CODES.AddressStateProvinceRegion), function (addressStateProvinceRegion) {
  return addressStateProvinceRegion.map(function (item) {
    return {
      Value: item.Name,
      Id: item.Value
    };
  });
});
export var TaxLevelCodesSelector = createSelector(MetadataCodeTypeSelector(CODES.TaxLevel), function (taxLevelCodes) {
  return taxLevelCodes.map(function (item) {
    return {
      Value: item.Name,
      Id: item.Value
    };
  });
});
var getValueFromAdditionalProperty = function getValueFromAdditionalProperty(key, object) {
  return find(propEq(key, 'Key'))(object.AdditionalProperties).Value;
};
var IsNewCustomerSelector = function IsNewCustomerSelector(state, isNewCustomer) {
  return isNewCustomer;
};
export var SubscriberTypeRoleSelector = createSelector(MetadataCodeTypeSelector(CODES.SubscriberTypeSecurityRole), function (subscriberTypeRole) {
  return subscriberTypeRole.filter(function (item) {
    return getBoolOrDefault(getValueFromAdditionalProperty('is_enabled', item));
  }).map(function (item) {
    return {
      subscriberType: getValueFromAdditionalProperty('subscriber_type_code', item),
      role: getValueFromAdditionalProperty('security_role_id', item)
    };
  });
});
export var SubscriberTypeFilterByUserRole = createSelector([MetadataCodeTypeSelector(CODES.SubscriberType), SubscriberTypeRoleSelector, UserSecurityRolesSelector, CurrentCustomerSelector, IsNewCustomerSelector], function (subscriberTypes, subscriberTypeRoles, roles, currentCustomer, isNewCustomer) {
  if (subscriberTypeRoles.length && roles.length && subscriberTypes.length) {
    var returnArray = subscriberTypes.filter(function (subscriberType) {
      return !subscriberTypeRoles.some(function (subscriberTypeRole) {
        return subscriberTypeRole.subscriberType === subscriberType.Value;
      });
    });
    var filteredElements = subscriberTypes.filter(function (subscriberType) {
      return subscriberTypeRoles.some(function (subscriberTypeRole) {
        return subscriberTypeRole.subscriberType === subscriberType.Value && roles.includes(parseInt(subscriberTypeRole.role));
      });
    });
    if (filteredElements && filteredElements.length) {
      returnArray = returnArray.concat(filteredElements);
    }
    if (!isNewCustomer && !returnArray.some(function (subscriberType) {
      return subscriberType.Value === currentCustomer.SubscriberTypeCode;
    })) {
      returnArray = returnArray.concat(subscriberTypes.filter(function (subscriberType) {
        return subscriberType.Value === currentCustomer.SubscriberTypeCode;
      }));
    }
    return returnArray;
  } else {
    return subscriberTypes && subscriberTypes.length ? subscriberTypes : [];
  }
});
export var SubscriberTypesWithItemsFilterByUserRole = createSelector([SubscriberTypeFilterByUserRole], function (subscriberTypes) {
  return {
    isLoaded: !!subscriberTypes && !!subscriberTypes.length,
    items: subscriberTypes
  };
});
export var CustomerTypesByCategoryOptionsSelector = createImmutableSelector([SubscriberTypeFilterByUserRole, MetadataCodeSelector(CODES.SubscriberCategoryDefaultSubscriberType)], function (subscriberTypes, subscriberCategoryDefaultSubscriberType) {
  var defaultSubscriberTypes = pathOr([], ['items'], subscriberCategoryDefaultSubscriberType).map(function (object) {
    return {
      subscriberCategory: getValueFromAdditionalProperty('subscriber_category_code', object),
      defaultSubscriberType: getValueFromAdditionalProperty('default_subscriber_type_code', object)
    };
  });
  return subscriberTypes.reduce(function (typeOptions, type) {
    var categoryCode = getValueFromAdditionalProperty('subscriber_category_code', type);
    var defaultSubscriberTypeCode = pathOr(null, ['defaultSubscriberType'], defaultSubscriberTypes.find(function (item) {
      return item.subscriberCategory === categoryCode;
    }));
    if (!typeOptions[categoryCode]) {
      typeOptions[categoryCode] = [];
    }
    typeOptions[categoryCode].push({
      name: type.Name,
      value: type.Value,
      default: type.Value === defaultSubscriberTypeCode
    });
    return typeOptions;
  }, {});
});
export var CustomerTypeFilteredSelector = createSelector([SubscriberTypesWithItemsFilterByUserRole, MetadataCodeSelector(CODES.SubscriberCategoryDefaultSubscriberType), SelectedCustomerCategorySelector, IsNewCustomerSelector, CurrentCustomerIsExternallyManagedARSelector, HasConvergentBillerIdSelector], function (subscriberType, subscriberCategoryDefaultSubscriberType, customerCategory, isNewCustomer, currentCustomerIsExternallyManagedAR, hasConvergentBillerId) {
  var subscriberTypes = [];
  if (!isNewCustomer && hasConvergentBillerId) {
    subscriberTypes = subscriberType.items.filter(function (item) {
      if (currentCustomerIsExternallyManagedAR) {
        return !!(item && item.AdditionalProperties.find(propEq('externally_managed_accounts_receivable', 'Key')).Value === 'True');
      } else {
        return !!(item && item.AdditionalProperties.find(propEq('externally_managed_accounts_receivable', 'Key')).Value !== 'True');
      }
    });
  } else if (subscriberType) {
    subscriberTypes = subscriberType.items;
  }

  // Defaulting to residential otherwise commercial, and returning the value in a string
  // to match the AdditionalProperty value
  var category = customerCategory === CUSTOMER_CATEGORY.COMMERCIAL ? '1' : '0';
  if (pathOr(false, ['items', 'length'], subscriberCategoryDefaultSubscriberType) > 0) {
    var subscriberCategoryDefaultSubscriberTypeOptions = subscriberCategoryDefaultSubscriberType.items.map(function (object) {
      return {
        subscriberCategory: getValueFromAdditionalProperty('subscriber_category_code', object),
        defaultSubscriberType: getValueFromAdditionalProperty('default_subscriber_type_code', object)
      };
    });
    var filterCategoryDefault = filter(propEq(category, 'subscriberCategory'), subscriberCategoryDefaultSubscriberTypeOptions);
    var categoryDefault = pathOr(null, ['defaultSubscriberType'], filterCategoryDefault[0]);
    var subscriberTypeOptions;
    if (categoryDefault) {
      subscriberTypeOptions = subscriberTypes.map(function (object) {
        return {
          subscriberCategory: getValueFromAdditionalProperty('subscriber_category_code', object),
          name: object.Name,
          value: object.Value,
          default: object.Value === categoryDefault
        };
      });
    } else {
      subscriberTypeOptions = [{
        name: i18n.translate(CoreLocaleKeys.SELECT),
        value: null,
        subscriberCategory: category,
        default: true
      }].concat(subscriberTypes.map(function (object) {
        return {
          subscriberCategory: getValueFromAdditionalProperty('subscriber_category_code', object),
          name: object.Name,
          value: object.Value,
          default: false
        };
      }));
    }
    return filter(propEq(category, 'subscriberCategory'), subscriberTypeOptions);
  } else {
    return [{
      name: i18n.translate(CoreLocaleKeys.SELECT),
      value: null,
      default: true
    }].concat(subscriberTypes.filter(function (subscriberType) {
      return category === getValueFromAdditionalProperty('subscriber_category_code', subscriberType);
    }).map(function (object) {
      return {
        name: object.Name,
        value: object.Value,
        default: false
      };
    }));
  }
});
export var DefaultCustomerTypeSelector = createSelector([CustomerTypeFilteredSelector], function (customerTypeOptions) {
  var defaultValue = pathOr(null, ['value'], find(propEq(true, 'default'))(customerTypeOptions));
  return defaultValue ? defaultValue : pathOr(null, ['value'], customerTypeOptions[0]);
});
export var CustomerTaxCategoryExemptionsSelector = createSelector([CurrentCustomerSelector], function (currentCustomer) {
  return pathOr([], ['TaxExemptionCategories'], currentCustomer);
});
export var CustomerTaxLevelExemptionsSelector = createSelector([CurrentCustomerSelector], function (currentCustomer) {
  return pathOr([], ['TaxExemption'], currentCustomer);
});
export var TaxLevelExemptionsSelector = createSelector([CustomerTaxLevelExemptionsSelector], function (taxLevels) {
  var selectedTaxExemptions = [];
  for (var key in taxLevels) {
    // eslint-disable-next-line no-prototype-builtins
    if (taxLevels.hasOwnProperty(key) && taxLevels[key] === true && key !== WHOLESALE_FLAG) {
      selectedTaxExemptions.push(key);
    }
  }
  return selectedTaxExemptions;
});
export var SelectedTaxLevelExemptionsSelector = createSelector([TaxLevelExemptionsSelector], function (taxLevelExemptions) {
  return taxLevelExemptionsHelper().filter(function (exemption) {
    return taxLevelExemptions.includes(exemption.value);
  });
});
export var ContactMethodOptionsSelector = createSelector([MetadataCodeTypeSelector(CODES.ContactMethod), MetadataCodeTypeSelector(CODES.ContactMapping)], function (contactMethods, eventMethodMap) {
  var invoiceMethods = filterByInvoiceEventType(eventMethodMap);
  return contactMethods.filter(function (item) {
    return invoiceMethods.includes(item.Value);
  });
});
var SUBSCRIBER_IDENTIFICATIONS_KEYS = {
  IS_COUNTRY_STATE_REQUIRED: 'is_country_and_state_required',
  IS_COUNTRY_STATE_VISIBLE: 'is_country_and_state_visible',
  IS_LUHN: 'is_luhn',
  IS_PRIMARY_IDENTIFICATION: 'is_primary_identification',
  REGEX: 'regex',
  REGEX_CODE: 'regex_code',
  USE_CONFIGURED_COUNTRIES: 'use_configured_countries',
  VALIDATION_TYPE_CODE: 'subscriber_identification_validation_type_code'
};
export var SubscriberIdentificationsSelector = createSelector([MetadataCodeTypeSelector(CODES.SubscriberIdentifications), MetadataCodeTypeSelector(CODES.SubscriberValidationTypes), MetadataCodeTypeSelector(CODES.RegularExpression), MetadataCodeTypeSelector(CODES.AddressCountry), MetadataCodeTypeSelector(CODES.AllCountry), SelectedCustomerSelector], function (subscriberIdentifications, validationTypes, regularExpressions, configuredCountries, allCountries, customer) {
  return subscriberIdentifications.map(function (subscriberIdentification) {
    var isPrimary = findByKey(SUBSCRIBER_IDENTIFICATIONS_KEYS.IS_PRIMARY_IDENTIFICATION, subscriberIdentification.AdditionalProperties);
    var isCountryStateRequired = findByKey(SUBSCRIBER_IDENTIFICATIONS_KEYS.IS_COUNTRY_STATE_REQUIRED, subscriberIdentification.AdditionalProperties);
    var isCountryStateVisible = getBoolOrDefault((findByKey(SUBSCRIBER_IDENTIFICATIONS_KEYS.IS_COUNTRY_STATE_VISIBLE, subscriberIdentification.AdditionalProperties) || isCountryStateRequired).Value, false);
    var useConfiguredCountries = findByKey(SUBSCRIBER_IDENTIFICATIONS_KEYS.USE_CONFIGURED_COUNTRIES, subscriberIdentification.AdditionalProperties) || true;
    var subscriberValidationType = findByKey(SUBSCRIBER_IDENTIFICATIONS_KEYS.VALIDATION_TYPE_CODE, subscriberIdentification.AdditionalProperties);
    var validationType = subscriberValidationType ? findByValue(subscriberValidationType.Value, validationTypes) : null;
    var validationTypeLuhn = validationType ? findByKey(SUBSCRIBER_IDENTIFICATIONS_KEYS.IS_LUHN, validationType.AdditionalProperties) : null;
    var validationTypeRegexCode = validationType ? findByKey(SUBSCRIBER_IDENTIFICATIONS_KEYS.REGEX_CODE, validationType.AdditionalProperties) : null;
    var regularExpression = validationTypeRegexCode ? findByValue(validationTypeRegexCode.Value, regularExpressions) : null;
    var regularExpressionValue = regularExpression ? findByKey(SUBSCRIBER_IDENTIFICATIONS_KEYS.REGEX, regularExpression.AdditionalProperties) : null;
    var isLuhn = getBoolOrDefault(validationTypeLuhn ? validationTypeLuhn.Value : null, false);
    return {
      Countries: isCountryStateVisible ? subscriberIdentificationCountryHelper(convertStringToNumber(subscriberIdentification.Value), configuredCountries, allCountries, customer) : EMPTY_ARRAY,
      IsPrimary: getBoolOrDefault(isPrimary.Value, false),
      IsCountryStateRequired: getBoolOrDefault(isCountryStateRequired.Value, false),
      IsCountryStateVisible: isCountryStateVisible,
      Name: subscriberIdentification.Name,
      Regex: !isLuhn && regularExpressionValue ? regularExpressionValue.Value : null,
      UseConfiguredCountries: getBoolOrDefault(useConfiguredCountries.Value, false),
      Value: convertStringToNumber(subscriberIdentification.Value)
    };
  });
});
var subscriberIdentificationCountryHelper = function subscriberIdentificationCountryHelper(subscriberIdentificationTypeCode, configuredCountries, allCountries, customer) {
  var subscriberIdentification = (customer.data.SubscriberIdentifications || []).find(function (identification) {
    return identification.SubscriberIdentificationTypeCode === subscriberIdentificationTypeCode;
  });
  if (subscriberIdentification && allCountries.length) {
    var selectedCountry = allCountries.find(function (country) {
      return country.Value === subscriberIdentification.Country;
    });
    var existingCountryInConfiguredCountries = configuredCountries.find(function (country) {
      return country.Value === selectedCountry.Value;
    });
    return mapCountrykeysHelper(existingCountryInConfiguredCountries ? configuredCountries : configuredCountries.concat(selectedCountry));
  } else {
    return mapCountrykeysHelper(configuredCountries);
  }
};
var mapCountrykeysHelper = function mapCountrykeysHelper(countries) {
  var map = {
    AdditionalProperties: 'AdditionalProperties',
    Global: 'Global',
    Name: 'label',
    Value: 'value'
  };
  return countries.map(function (country) {
    return mapKeys(country, function (value, key) {
      return map[key];
    });
  });
};
var filterByInvoiceEventType = function filterByInvoiceEventType(eventMethodMap) {
  var invoiceContactMethods = eventMethodMap.filter(function (option) {
    var invoiceTypeCode = option.AdditionalProperties.find(propEq('contact_event_type_code', 'Key'));
    return invoiceTypeCode.Value === INVOICE_DELIVERY_METHODS.INVOICE;
  });
  return invoiceContactMethods.map(function (item) {
    return item.AdditionalProperties.find(propEq('contact_method_code', 'Key')).Value;
  });
};
var shouldDisplayAnonymizeOptions = function shouldDisplayAnonymizeOptions(isDbss, customerState) {
  var displayAnonymizeOptions = true;
  if (isDbss) {
    var coercedState = convertStringToNumber(customerState);
    displayAnonymizeOptions = coercedState === CUSTOMER_STATE.PROSPECT || coercedState === CUSTOMER_STATE.DISCONNECTED || coercedState === PROSPECT_LITE_CUSTOMER;
  }
  return displayAnonymizeOptions;
};